
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DocumentServerSettings struct
 */
class DocumentServerSettings {
  /**
   * Initialize struct
   * @param {!number=} documentPort
   * @param {!boolean=} useSsl
   * @param {!number=} legalDocumentsSlowQueryLogThreshold
   * @constructor
   */
  constructor (documentPort = 6190, useSsl = false, legalDocumentsSlowQueryLogThreshold = new UInt64(500000000, 0)) {
    this.DocumentPort = documentPort
    this.UseSsl = useSsl
    this.LegalDocumentsSlowQueryLogThreshold = legalDocumentsSlowQueryLogThreshold
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DocumentServerSettings}
   * @param {!DocumentServerSettings} other Other struct
   * @returns {!DocumentServerSettings} This struct
   */
  copy (other) {
    if (other.DocumentPort != null) {
      this.DocumentPort = other.DocumentPort
    } else {
      this.DocumentPort = null
    }
    if (other.UseSsl != null) {
      this.UseSsl = other.UseSsl
    } else {
      this.UseSsl = null
    }
    if (other.LegalDocumentsSlowQueryLogThreshold != null) {
      this.LegalDocumentsSlowQueryLogThreshold = UInt64.fromNumber(other.LegalDocumentsSlowQueryLogThreshold)
    } else {
      this.LegalDocumentsSlowQueryLogThreshold = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DocumentServerSettings}
   * @returns {!DocumentServerSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DocumentServerSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DocumentServerSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DocumentServerSettings}
   * @param {!DocumentServerSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DocumentServerSettings)) {
      throw new TypeError('Instance of DocumentServerSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DocumentServerSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DocumentPort: ((this.DocumentPort != null) ? this.DocumentPort : null),
      UseSsl: ((this.UseSsl != null) ? this.UseSsl : null),
      LegalDocumentsSlowQueryLogThreshold: ((this.LegalDocumentsSlowQueryLogThreshold != null) ? this.LegalDocumentsSlowQueryLogThreshold.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DocumentServerSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DocumentServerSettings} other Object value
   * @returns {!DocumentServerSettings} Created struct
   */
  static fromObject (other) {
    return new DocumentServerSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DocumentServerSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DocumentServerSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DocumentServerSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 217
  }
}

export { DocumentServerSettings };

/**
 * Fast Binary Encoding DocumentServerSettings field model
 */
class FieldModelDocumentServerSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DocumentPort = new fbe.FieldModelUInt16(buffer, 4 + 4)
    this.UseSsl = new fbe.FieldModelBool(buffer, this.DocumentPort.fbeOffset + this.DocumentPort.fbeSize)
    this.LegalDocumentsSlowQueryLogThreshold = new fbe.FieldModelUInt64(buffer, this.UseSsl.fbeOffset + this.UseSsl.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DocumentPort.fbeSize + this.UseSsl.fbeSize + this.LegalDocumentsSlowQueryLogThreshold.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DocumentPort.fbeExtra + this.UseSsl.fbeExtra + this.LegalDocumentsSlowQueryLogThreshold.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDocumentServerSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 217
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDocumentServerSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDocumentServerSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentPort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DocumentPort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentPort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSsl.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    if ((fbeCurrentSize + this.LegalDocumentsSlowQueryLogThreshold.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LegalDocumentsSlowQueryLogThreshold.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LegalDocumentsSlowQueryLogThreshold.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDocumentServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDocumentServerSettings}
   * @param {!DocumentServerSettings} fbeValue Default value, defaults is new DocumentServerSettings()
   * @returns {!DocumentServerSettings} DocumentServerSettings value
   */
  get (fbeValue = new DocumentServerSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDocumentServerSettings}
   * @param {!DocumentServerSettings} fbeValue DocumentServerSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentPort.fbeSize) <= fbeStructSize) {
      fbeValue.DocumentPort = this.DocumentPort.get(6190)
    } else {
      fbeValue.DocumentPort = 6190
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentPort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) <= fbeStructSize) {
      fbeValue.UseSsl = this.UseSsl.get(false)
    } else {
      fbeValue.UseSsl = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    if ((fbeCurrentSize + this.LegalDocumentsSlowQueryLogThreshold.fbeSize) <= fbeStructSize) {
      fbeValue.LegalDocumentsSlowQueryLogThreshold = this.LegalDocumentsSlowQueryLogThreshold.get(new UInt64(500000000, 0))
    } else {
      fbeValue.LegalDocumentsSlowQueryLogThreshold = new UInt64(500000000, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LegalDocumentsSlowQueryLogThreshold.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDocumentServerSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDocumentServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDocumentServerSettings}
   * @param {!DocumentServerSettings} fbeValue DocumentServerSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDocumentServerSettings}
   * @param {DocumentServerSettings} fbeValue DocumentServerSettings value
   */
  setFields (fbeValue) {
    this.DocumentPort.set(fbeValue.DocumentPort)
    this.UseSsl.set(fbeValue.UseSsl)
    this.LegalDocumentsSlowQueryLogThreshold.set(fbeValue.LegalDocumentsSlowQueryLogThreshold)
  }
}

export { FieldModelDocumentServerSettings };

/**
 * Fast Binary Encoding DocumentServerSettings model
 */
class DocumentServerSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDocumentServerSettings(this.buffer, 4)
  }

  /**
   * Get the DocumentServerSettings model
   * @this {!DocumentServerSettingsModel}
   * @returns {!FieldModelDocumentServerSettings} model DocumentServerSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DocumentServerSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DocumentServerSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DocumentServerSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DocumentServerSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDocumentServerSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DocumentServerSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DocumentServerSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DocumentServerSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DocumentServerSettingsModel}
   * @param {!DocumentServerSettings} value DocumentServerSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DocumentServerSettingsModel}
   * @param {!DocumentServerSettings} value DocumentServerSettings value, defaults is new DocumentServerSettings()
   * @return {!object} Deserialized DocumentServerSettings value and its size
   */
  deserialize (value = new DocumentServerSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DocumentServerSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DocumentServerSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DocumentServerSettingsModel}
   * @param {!number} prev Previous DocumentServerSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DocumentServerSettingsModel };
